import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { usePagination } from '@web/app/shared';
import { type RouteContext, createNavigator, extractRouteParams } from '@web/app/shared/utils/route-utils';

import { useAgent, useAgents } from '@codes/agents';

interface MyAgentsParams extends RouteContext {
    id?: string;
}

export const useMyAgents = () => {
    const { t } = useTranslation();
    const routeParams = extractRouteParams<MyAgentsParams>(useParams());
    const { id: agentId, workspaceId, projectId } = routeParams;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const navigator = createNavigator(navigate, { workspaceId, projectId });

    const isCreateMode = searchParams.get('mode') === 'create';
    const searchQuery = searchParams.get('search') || '';
    const page = parseInt(searchParams.get('page') || '0', 10);
    const limit = 10;

    const { data: agentsData, isLoading: isAgentsLoading } = useAgents({
        // search: searchQuery,
        view: 'mine',
        page,
        limit,
    });
    const { data: selectedAgent, isLoading: isAgentLoading } = useAgent(agentId || '', !!agentId);

    const { paginationRange } = usePagination({
        totalCount: agentsData?.total || 0,
        pageSize: limit,
        currentPage: page,
        siblingCount: 1,
    });

    const isFormVisible = Boolean(agentId) || isCreateMode;
    const formMode = agentId ? 'edit' : 'create';

    const handleAgentSelect = (selectedAgentId: string) => {
        const currentParams = new URLSearchParams(searchParams);
        navigator.navigateToWithQuery(`/my-agents/${selectedAgentId}`, currentParams);
    };

    const handleCreateNew = () => {
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set('mode', 'create');
        navigator.navigateToWithQuery('/my-agents', currentParams);
    };

    const handleFormSuccess = (newAgentId: string) => {
        if (isCreateMode) {
            const currentParams = new URLSearchParams(searchParams);
            currentParams.delete('mode'); // create 모드는 제거
            navigator.navigateToWithQuery(`/my-agents/${newAgentId}`, currentParams);
        }
    };

    const handlePageChange = (newPage: number) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        });
    };

    const handleSearchChange = (query: string) => {
        setSearchParams(prev => {
            if (query) {
                prev.set('search', query);
            } else {
                prev.delete('search');
            }
            prev.set('page', '0');
            return prev;
        });
    };

    return {
        agentId,
        isCreateMode,
        searchQuery,
        page,
        limit,
        agentsData,
        isAgentsLoading,
        selectedAgent,
        isAgentLoading,
        paginationRange: paginationRange || [],
        isFormVisible,
        formMode,
        handleAgentSelect,
        handleCreateNew,
        handleFormSuccess,
        handlePageChange,
        handleSearchChange,
    };
};
